import { useState } from 'react';
import { Calendar, ChevronRight, Sparkles, Play } from 'lucide-react';
import { InsetGroup, InsetRow } from '../../components/boothly/inset-list.jsx';
import { ActionButton } from '../../action-button.jsx';
import { NeuerMarkttagSheet } from './neuer-markttag-sheet.jsx';
import { MarktagDetailSheet } from './marktag-detail-sheet.jsx';
import { EmptyState, SkeletonTroedelDay } from '../../components/boothly/index.js';
import { Portal } from '../../components/boothly/portal.jsx';
import { useAnySheetOpen } from '../../widgets.jsx';
import { fmtCurrency } from '../../lib/format.js';

function formatDuration(startTime) {
  // clamp gegen Future/Null-Start → kein Riesenwert ("11 Std 52 Min" ab Sekunde 1)
  const diff = Math.max(0, Date.now() - new Date(startTime).getTime());
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return hours ? `${hours} Std. ${minutes} Min.` : `${minutes} Min.`;
}

const fmtDay = (iso) => new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

// Finale Zahlen eines (vergangenen) Markttags: aus eingebetteten sales (include_stats=1
// liefert sales(total,is_voided,is_bundle)) rechnen; Fallback auf gespeicherte Aggregate/Seed.
function sessionStats(market) {
  const sales = Array.isArray(market.sales) ? market.sales.filter(s => !s.is_voided) : null;
  const revenue = sales != null
    ? sales.reduce((sum, s) => sum + (s.total || 0), 0)
    : (market.revenue ?? market.total_revenue ?? null);
  const count = sales != null
    ? sales.length
    : (market.sales_count ?? null);
  return { revenue, count };
}

export function Markttage({ runningMarket, pastMarkets, plannedMarkets, onOpenLiveMarkt, onCreateSession, onStartPlanned, onUpdateBoothFee, dark, loading, store, authToken }) {
  const [newMarketOpen, setNewMarketOpen] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const anySheetOpen = useAnySheetOpen();

  if (loading) {
    return (
      <div className="space-y-4 pt-2">
        {/* Running market skeleton — matched shape of the running market card */}
        <div className="bg-[var(--bg-L1)] border border-[var(--border-default)] rounded-2xl p-5 animate-pulse">
          <div className="h-3 w-20 rounded-full bg-[var(--fill-tertiary)] mb-4" />
          <div className="h-5 w-44 rounded bg-[var(--fill-tertiary)] mb-2" />
          <div className="h-3 w-32 rounded bg-[var(--fill-tertiary)] mb-5" />
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-[var(--bg-L2)] rounded-xl h-16" />
            <div className="bg-[var(--bg-L2)] rounded-xl h-16" />
          </div>
          <div className="h-12 rounded-xl bg-[var(--fill-tertiary)]" />
        </div>
        {[0, 1, 2].map(i => (
          <SkeletonTroedelDay key={i} />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="screen-troedel-tage" className="space-y-7 pt-2" style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}>
      {/* Sticky "Neuer Markttag" — bleibt beim Scrollen unter dem Header sichtbar */}
      <ActionButton
        variant="sticky"
        accent="var(--accent-troedel)"
        label="Neuer Markttag"
        onClick={() => setNewMarketOpen(true)}
      />

      {/* Laufender Markt */}
      {runningMarket && (
        <section className="rounded-[20px] bg-[var(--bg-L1)] p-4">
          <p className="flex items-center gap-1.5 text-[13px] text-[var(--label-secondary)]">
            <span className="h-[7px] w-[7px] rounded-full bg-[var(--accent-green)]" aria-hidden="true" />
            <span className="font-semibold text-[var(--accent-green)]">Markt läuft</span>
            {(runningMarket.startTime || runningMarket.starts_at) && (
              <span>{`· seit ${formatDuration(runningMarket.startTime || runningMarket.starts_at)}`}</span>
            )}
          </p>
          <h3 className="mt-1 truncate text-[20px] font-semibold leading-tight">{runningMarket.location || runningMarket.name}</h3>
          <div className="mt-3 flex items-baseline justify-between">
            <div>
              <p className="text-[13px] text-[var(--label-secondary)]">Umsatz</p>
              <p className="num text-[28px] font-bold leading-tight">{fmtCurrency(runningMarket.revenue ?? 0)}</p>
            </div>
            <div className="text-right">
              <p className="text-[13px] text-[var(--label-secondary)]">Verkäufe</p>
              <p className="num text-[28px] font-semibold leading-tight">{runningMarket.salesCount ?? 0}</p>
            </div>
          </div>
          <button
            onClick={onOpenLiveMarkt}
            className="mt-4 flex h-[50px] w-full items-center justify-center gap-2 rounded-[14px] bg-[var(--accent-troedel)] text-[17px] font-semibold text-white shadow-[var(--shine-button)] active:scale-[0.98] active:opacity-90"
            style={{ transition: 'transform 100ms var(--ease-out-ios), opacity 100ms' }}
          >
            Zum Live-Markt
          </button>
        </section>
      )}

      {/* Geplant */}
      {(plannedMarkets ?? []).length > 0 && (
        <InsetGroup title="Geplant">
          {(plannedMarkets ?? []).map((market, idx) => (
            <InsetRow
              key={market.id}
              first={idx === 0}
              data-testid="planned-market-card"
              minHeight={60}
              leading={
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--tint-troedel)]">
                  <Calendar className="h-[18px] w-[18px] text-[var(--accent-troedel)]" />
                </span>
              }
              title={<span className="block truncate">{market.location || market.name}</span>}
              subtitle={market.starts_at ? fmtDay(market.starts_at) : null}
              trailing={
                <button
                  onClick={() => onStartPlanned?.(market)}
                  className="flex h-11 shrink-0 items-center gap-1.5 rounded-full bg-[var(--tint-troedel)] px-4 text-[15px] font-semibold text-[var(--accent-troedel)] active:scale-[0.97]"
                  style={{ transition: 'transform 100ms var(--ease-out-ios)' }}
                >
                  <Play className="h-4 w-4" fill="currentColor" />
                  Starten
                </button>
              }
            />
          ))}
        </InsetGroup>
      )}

      {/* Vergangene Märkte */}
      {(pastMarkets ?? []).length > 0 && (
        <InsetGroup title="Vergangene Märkte" aside={(pastMarkets ?? []).length}>
          {(pastMarkets ?? []).map((market, idx) => {
            const stats = sessionStats(market);
            const day = market.date || (market.starts_at ? fmtDay(market.starts_at) : '');
            return (
              <InsetRow
                key={market.id}
                first={idx === 0}
                testId="market-day-card"
                data-date={market.starts_at ? market.starts_at.slice(0, 10) : ''}
                onClick={() => setSelectedMarket(market)}
                minHeight={64}
                title={
                  <span className="flex items-center gap-1.5">
                    <span className="truncate">{market.location || market.name || day}</span>
                    {market.isTopDay && <Sparkles className="h-4 w-4 shrink-0 text-[var(--accent-orange)]" />}
                  </span>
                }
                subtitle={`${day} · ${stats.count ?? 0} Verkäufe`}
                value={stats.revenue != null ? fmtCurrency(stats.revenue) : ''}
                valueClassName="font-semibold"
                trailing={<ChevronRight className="h-4 w-4 shrink-0 text-[var(--label-tertiary)]" strokeWidth={2.6} />}
              />
            );
          })}
        </InsetGroup>
      )}

      {/* Empty state */}
      {!runningMarket && (pastMarkets ?? []).length === 0 && (plannedMarkets ?? []).length === 0 && (
        <EmptyState
          icon={Calendar}
          title="Noch keine Markttage"
          description="Starte deinen ersten Markttag, um Verkäufe zu erfassen."
          cta={{
            label: 'Markttag starten',
            accent: 'troedel',
            onClick: () => setNewMarketOpen(true),
          }}
        />
      )}

      {/* Sheets — via Portal, damit position:fixed relativ zum Viewport ist (über dem Header)
          und nicht von der SwipeableTabs-Transform unter den Header geklemmt wird. */}
      <Portal>
        <NeuerMarkttagSheet
          open={newMarketOpen}
          onOpenChange={setNewMarketOpen}
          onCreateSession={onCreateSession}
          dark={dark}
        />
        <MarktagDetailSheet
          market={selectedMarket}
          open={!!selectedMarket}
          onOpenChange={(o) => { if (!o) setSelectedMarket(null); }}
          onUpdateBoothFee={onUpdateBoothFee}
          dark={dark}
          storeId={store?.id}
          authToken={authToken}
        />
      </Portal>
    </div>
  );
}
